import { useCallback, useEffect, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/input'
import { Dialog } from '@/components/ui/dialog'
import { Skeleton } from '@/components/States'
import { RefreshButton } from '@/components/RefreshButton'
import { useToast } from '@/providers/UiProviders'
import {
  fetchRedisStats,
  clearCache,
  SYS_STR as S,
  type CacheScope,
  type RedisStats,
} from './systemConfigApi'

/**
 * A24.10 — Kesh boshqaruvi. REAL operational screen: reads Redis/cache health
 * (`GET /admin/infra/redis`, A17.15) and flushes the in-process config cache
 * (`POST /admin/devtools/cache/clear`, A27.11). No fake data — every metric is a
 * live Redis INFO field; the clear action is audited server-side.
 */
export function CacheManagement() {
  const { toast } = useToast()
  const [stats, setStats] = useState<RedisStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [scope, setScope] = useState<CacheScope>('all')
  const [confirm, setConfirm] = useState(false)
  const [clearing, setClearing] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchRedisStats(ac.signal)
      .then((d) => {
        if (alive) setStats(d)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const doClear = useCallback(async () => {
    setConfirm(false)
    setClearing(true)
    try {
      await clearCache(scope)
      toast(S.cacheCleared, { tone: 'success' })
      setReload((n) => n + 1)
    } catch {
      toast(S.cacheClearError, { tone: 'error' })
    } finally {
      setClearing(false)
    }
  }, [scope, toast])

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={S.cacheTitle}
        screenId="A24.10"
        description={S.cacheDesc}
        verified
        actions={<RefreshButton onClick={() => setReload((n) => n + 1)} />}
      />

      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        {/* Redis health */}
        {loading ? (
          <div className="grid max-w-3xl grid-cols-2 gap-2 sm:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : !stats ? (
          <div className="rounded-md border border-border-default bg-surface-2 px-3 py-6 text-center text-sm text-danger">
            {S.redisDown}
          </div>
        ) : (
          <div className="grid max-w-3xl grid-cols-2 gap-2 sm:grid-cols-4">
            <Metric label={S.mVersion} value={stats.version || '—'} />
            <Metric label={S.mUptime} value={formatUptime(stats.uptime_seconds)} />
            <Metric label={S.mClients} value={stats.connected_clients.toLocaleString()} />
            <Metric
              label={S.mMemory}
              value={formatBytes(stats.used_memory)}
              sub={stats.maxmemory > 0 ? `${stats.used_memory_pct.toFixed(1)}%` : undefined}
            />
            <Metric label={S.mHitRate} value={`${stats.hit_rate.toFixed(1)}%`} accent />
            <Metric label={S.mKeys} value={stats.db_keys.toLocaleString()} />
            <Metric label={S.mOps} value={stats.ops_per_sec.toLocaleString()} />
            <Metric label={S.mEvicted} value={stats.evicted_keys.toLocaleString()} />
            <Metric label={S.mPolicy} value={stats.maxmemory_policy || '—'} />
          </div>
        )}

        {/* Clear cache action */}
        <section className="max-w-3xl rounded-md border border-border-default bg-surface-2 p-3">
          <h2 className="mb-3 text-sm font-medium text-secondary">{S.cacheClear}</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-secondary">{S.cacheScope}</span>
              <Select
                value={scope}
                onChange={(e) => setScope(e.target.value as CacheScope)}
                className="w-48"
              >
                <option value="all">{S.cacheScopeAll}</option>
                <option value="config">{S.cacheScopeConfig}</option>
                <option value="users">{S.cacheScopeUsers}</option>
              </Select>
            </label>
            <Button variant="danger" onClick={() => setConfirm(true)} disabled={clearing}>
              {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {clearing ? S.cacheClearing : S.cacheClear}
            </Button>
          </div>
        </section>
      </div>

      <Dialog open={confirm} onClose={() => setConfirm(false)} title={S.confirmClearTitle}>
        <p className="text-sm text-secondary">{S.confirmClear}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirm(false)}>
            {S.cancel}
          </Button>
          <Button variant="danger" onClick={doClear}>
            {S.cacheClear}
          </Button>
        </div>
      </Dialog>
    </div>
  )
}

function Metric({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-1 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      <div className={`mt-0.5 font-mono text-lg tabular ${accent ? 'text-accent' : 'text-primary'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-tertiary">{sub}</div>}
    </div>
  )
}

function formatBytes(n: number): string {
  if (!n || n < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function formatUptime(sec: number): string {
  if (!sec || sec < 0) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}
